// Shared sensitive-data scrubbing for Sentry events and error context.
// Used by FE beforeSend, edge function ctx.fetch, and Cloud Run reporters.

const REDACTED = '[REDACTED]';

const JWT_RE = /eyJ[\w-]+\.[\w-]+\.[\w-]+/g;
const BEARER_RE = /Bearer\s+\S+/gi;
const PASSWORD_RE = /password["']?\s*[:=]\s*["']?\S+/gi;

const SENSITIVE_HEADERS = new Set([
  'authorization', 'apikey', 'x-api-key', 'api-key', 'cookie',
]);

const SENSITIVE_PARAMS = new Set([
  'api_token', 'key', 'token', 'secret', 'password', 'apikey',
  // Not a secret — PII. `GeocodingClient` puts a company's full street address
  // here, and a non-2xx reply from Google is reported to Sentry with the
  // request URL as the event title (`_shared/handler.ts` instrumented fetch).
  'address',
  // OAuth callback params. `code` is a live, redeemable Google authorization
  // code and `state` is the CSRF token — both land in `window.location.href`
  // at `/google-callback`, which Sentry attaches as `event.request.url`.
  'code', 'state',
]);

const SENSITIVE_FIELDS = new Set([
  'password', 'refresh_token', 'access_token', 'client_secret', 'api_token', 'apikey',
  // Outbound-email payloads (PII: vendor addresses, invoice/amount details in the body).
  'html_body', 'recipients', 'cc',
]);

/** Scrub JWTs, Bearer tokens, and password patterns from a string. */
export function scrubString(value: string): string {
  return value
    .replace(JWT_RE, REDACTED)
    .replace(BEARER_RE, `Bearer ${REDACTED}`)
    .replace(PASSWORD_RE, `password=${REDACTED}`);
}

// Param-name prefixes whose VALUES carry PII even though the name itself is
// not secret. `tbl_<resourceKey>` holds operator list filter + search values
// (freight searches routinely include NIP / company names).
const SENSITIVE_PARAM_PREFIXES = ['tbl_'];

function isSensitiveParam(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_PARAMS.has(lower) || SENSITIVE_PARAM_PREFIXES.some((p) => lower.startsWith(p));
}

/** Redact sensitive query parameters from a URL. */
export function scrubUrl(url: string | URL | Request): string {
  const raw = typeof url === 'string' ? url : url instanceof URL ? url.toString() : url.url;
  try {
    const u = new URL(raw);
    for (const key of [...u.searchParams.keys()]) {
      if (isSensitiveParam(key)) {
        u.searchParams.set(key, REDACTED);
      }
    }
    // Decode %5BREDACTED%5D back to [REDACTED] for readability
    return decodeURIComponent(u.toString());
  } catch {
    return scrubString(raw);
  }
}

/** Redact values of sensitive headers. Returns a plain object. */
export function scrubHeaders(headers?: HeadersInit | null): Record<string, string> | undefined {
  if (!headers) return undefined;
  const entries: [string, string][] =
    headers instanceof Headers
      ? [...headers.entries()]
      : Array.isArray(headers)
        ? headers
        : Object.entries(headers);
  const result: Record<string, string> = {};
  for (const [key, value] of entries) {
    result[key] = SENSITIVE_HEADERS.has(key.toLowerCase()) ? REDACTED : scrubString(value);
  }
  return result;
}

/** Deep-redact sensitive fields from an already-parsed payload object (any
 *  depth). Use for structured Sentry context (e.g. `requestPayload`) where the
 *  body is a nested object — `scrubBody` only handles a top-level JSON string. */
export function scrubPayload(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(scrubPayload);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (SENSITIVE_FIELDS.has(key.toLowerCase())) out[key] = REDACTED;
      else if (typeof v === 'string') out[key] = v.replace(JWT_RE, REDACTED);
      else out[key] = scrubPayload(v);
    }
    return out;
  }
  return value;
}

/** Redact sensitive fields from a request body string/object. */
export function scrubBody(body?: BodyInit | null): string | undefined {
  if (!body) return undefined;
  if (typeof body !== 'string') return undefined; // skip Blob/FormData/streams
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed !== null && typeof parsed === 'object') {
      const obj = parsed as Record<string, unknown>;
      for (const key of Object.keys(obj)) {
        const value = obj[key];
        if (SENSITIVE_FIELDS.has(key.toLowerCase())) {
          obj[key] = REDACTED;
        } else if (typeof value === 'string') {
          // Scrub JWTs embedded in string values (field-level redaction already handles keys)
          obj[key] = value.replace(JWT_RE, REDACTED);
        }
      }
      return JSON.stringify(parsed);
    }
    // A bare JSON string body has no fields to redact — scrub it as a string.
    if (typeof parsed === 'string' && parsed.length > 0) return scrubString(body);
    // Primitives (number/boolean/null) round-trip untouched.
    return JSON.stringify(parsed);
  } catch {
    // Non-JSON body (e.g. form-encoded) — apply full string scrubbing
    return scrubString(body);
  }
}
